import { usePendingInvite, useUser } from '@/app/redux/hooks/user';
import {
  selectAuthInitialized,
  selectClaims,
  selectIsAuthenticated,
  selectIsEmailVerified,
} from '@/app/redux/slices/sessionSlice';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import ModalProvider from '@/common/ui/modals/ModalProvider';
import { handleStudentLogout } from '@/common/utils/auth';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import { saveToLocalStorage } from '@/common/utils/localStorage';
import api from '@/db/apiClient';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from './redux/hooks';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AdminAuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();

  const { user, isLoading: isUserLoading } = useUser();
  const { pendingInvite, isLoading: isPendingInviteLoading } =
    usePendingInvite();

  const claims = useAppSelector(selectClaims);

  const authInitialized = useAppSelector(selectAuthInitialized);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isEmailVerified = useAppSelector(selectIsEmailVerified);

  const hasOrganisation = user && user.organisationIds.length > 0;

  const currentPath = location.pathname;

  const publicRoutes = [
    '/login',
    '/signup',
    '/forgot-password',
    '/reset-password',
  ];
  const isPublicRoute = publicRoutes.includes(currentPath);

  useEffect(() => {
    // Log student out if logged in
    const isStudentAuthed = !!api.asStudent.auth.currentUser;
    if (isStudentAuthed) {
      handleStudentLogout();
    }
  }, []);

  if (!authInitialized) {
    return (
      <div className="h-svh w-full flex items-center justify-center">
        <DataLoader />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (!isPublicRoute) {
      saveToLocalStorage('redirect', currentPath);
      return <Navigate to="/login" replace />;
    }
  } else if (isUserLoading || isPendingInviteLoading || !claims) {
    return (
      <div className="h-svh w-full flex items-center justify-center">
        <DataLoader />
      </div>
    );
  } else if (!isEmailVerified) {
    if (currentPath !== '/verify') {
      return <Navigate to="/verify" replace />;
    }
  } else if (pendingInvite) {
    if (!/^\/invite\/.+$/.test(currentPath)) {
      return <Navigate to={`/invite/${pendingInvite}`} replace />;
    }
  } else if (!hasOrganisation) {
    if (currentPath !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
  } else if (
    [...publicRoutes, '/onboarding', '/verify'].some((route) =>
      currentPath.startsWith(route),
    )
  ) {
    return <Navigate to={getDefaultDashboardRoute()} replace />;
  }

  // Allow access to the requested route
  return <ModalProvider>{children}</ModalProvider>;
}
